// src/admin/components/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext'; // Import useAuth to get user data

// Import new dashboard widget components
import SalesSummaryCard from './SalesSummaryCard';
import RevenueTarget from './RevenueTarget';
import SalesStatisticsChart from './SalesStatisticsChart';
import LatestOrdersTable from './LatestOrdersTable';
import BusinessGrowthByCountry from './BusinessGrowthByCountry';

// Define interfaces for your dashboard data, matching your backend structure
interface DashboardSummary {
  totalSales: number;
  totalOrders: number;
  ordersComplete: number;
  cancelOrders: number;
  totalSalesTarget: number; // Example for progress bar calculation
}

interface RevenueTargetData {
  currentPercentage: number;
  revenueToday: number; // Changed to number for calculation
  currentSellsRevenuePercentage: number; // Changed to number
  targetValue: number;
  revenueValue: number;
  sellsValue: number;
}

interface Order {
  id: string; // Assuming UUID or string ID from backend
  source: string;
  customerName: string;
  customerEmail: string;
  total: number;
  status: 'Processing' | 'Completed' | 'Cancelled';
}

interface SalesChartData {
  name: string; // e.g., 'Jan', 'Feb'
  Revenue: number;
  Sales: number;
  Cancel: number;
}

interface CountryGrowthData {
  country: string;
  flag: string;
  revenue: number; // Changed to number
  growthPercentage: number; // Changed to number for calculation
}


const AdminDashboard: React.FC = () => {
  const { user } = useAuth(); // Get user object from AuthContext
  const [currentDate, setCurrentDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for dynamic dashboard data
  const [summaryData, setSummaryData] = useState<DashboardSummary | null>(null);
  const [revenueTargetData, setRevenueTargetData] = useState<RevenueTargetData | null>(null);
  const [salesChartData, setSalesChartData] = useState<SalesChartData[]>([]);
  const [latestOrders, setLatestOrders] = useState<Order[]>([]);
  const [businessGrowth, setBusinessGrowth] = useState<CountryGrowthData[]>([]);

  useEffect(() => {
    // Set current date
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    setCurrentDate(date.toLocaleDateString('en-US', options));

    // Function to fetch all dashboard data
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        // --- Fetch Summary Data ---
        // Replace with your actual API endpoint for summary data
        // const summaryResponse = await fetch('/api/dashboard/summary');
        // if (!summaryResponse.ok) throw new Error('Failed to fetch summary data');
        // const summaryJson = await summaryResponse.json();
        // setSummaryData(summaryJson);
        setSummaryData({ // Mock data for now
          totalSales: 1200000000, // 1.2M
          totalOrders: 16920,
          ordersComplete: 16581,
          cancelOrders: 338,
          totalSalesTarget: 1600000000, // Example target for calculation
        });


        // --- Fetch Revenue Target Data ---
        // Replace with your actual API endpoint for revenue target
        // const revenueTargetResponse = await fetch('/api/dashboard/revenue-target');
        // if (!revenueTargetResponse.ok) throw new Error('Failed to fetch revenue target data');
        // const revenueTargetJson = await revenueTargetResponse.json();
        // setRevenueTargetData(revenueTargetJson);
        setRevenueTargetData({ // Mock data for now
          currentPercentage: 62.28,
          revenueToday: -740000, // -0.74M
          currentSellsRevenuePercentage: 40,
          targetValue: 1200000, // AED 1.2M
          revenueValue: 298000, // AED 298K
          sellsValue: 747000, // AED 747K
        });

        // --- Fetch Sales Chart Data ---
        // Replace with your actual API endpoint for sales chart data
        // const salesChartResponse = await fetch('/api/dashboard/sales-chart');
        // if (!salesChartResponse.ok) throw new Error('Failed to fetch sales chart data');
        // const salesChartJson = await salesChartResponse.json();
        // setSalesChartData(salesChartJson);
        setSalesChartData([ // Mock data for now
          { name: 'Jan', Revenue: 400, Sales: 240, Cancel: 100 },
          { name: 'Feb', Revenue: 300, Sales: 139, Cancel: 120 },
          { name: 'Mar', Revenue: 200, Sales: 980, Cancel: 80 },
          { name: 'Apr', Revenue: 278, Sales: 390, Cancel: 150 },
          { name: 'May', Revenue: 189, Sales: 480, Cancel: 90 },
          { name: 'Jun', Revenue: 239, Sales: 380, Cancel: 110 },
          { name: 'Jul', Revenue: 349, Sales: 430, Cancel: 70 },
          { name: 'Aug', Revenue: 450, Sales: 290, Cancel: 130 },
          { name: 'Sep', Revenue: 380, Sales: 500, Cancel: 100 },
          { name: 'Oct', Revenue: 250, Sales: 300, Cancel: 140 },
          { name: 'Nov', Revenue: 400, Sales: 270, Cancel: 90 },
          { name: 'Dec', Revenue: 500, Sales: 400, Cancel: 110 },
        ]);


        // --- Fetch Latest Orders ---
        // Replace with your actual API endpoint for latest orders
        // const ordersResponse = await fetch('/api/dashboard/latest-orders');
        // if (!ordersResponse.ok) throw new Error('Failed to fetch latest orders');
        // const ordersJson = await ordersResponse.json();
        // setLatestOrders(ordersJson);
        setLatestOrders([ // Mock data for now
          { id: 'ORD001', source: 'On Shop', customerName: 'Ibnul Shams Al Asad', customerEmail: 'shamsa@hcmed.com', total: 812100, status: 'Processing' },
          { id: 'ORD002', source: 'Amazon', customerName: 'Ilham Budi Agung', customerEmail: 'dahnbudis@google.com', total: 1349000, status: 'Processing' },
          { id: 'ORD003', source: 'On Shop', customerName: 'Mohammad Karim', customerEmail: 'm_karim@yahoo.com', total: 27250, status: 'Cancelled' },
          { id: 'ORD004', source: 'On Shop', customerName: 'Layla Al-Mansoori', customerEmail: 'layla@example.com', total: 50000, status: 'Completed' },
          { id: 'ORD005', source: 'Amazon', customerName: 'Fatima Zahra', customerEmail: 'fatima@mail.com', total: 120000, status: 'Processing' },
        ]);

        // --- Fetch Business Growth Data ---
        // Replace with your actual API endpoint for business growth
        // const growthResponse = await fetch('/api/dashboard/business-growth');
        // if (!growthResponse.ok) throw new Error('Failed to fetch business growth data');
        // const growthJson = await growthResponse.json();
        // setBusinessGrowth(growthJson);
        setBusinessGrowth([ // Mock data for now
          { country: 'UAE', flag: '🇦🇪', revenue: 6120000, growthPercentage: 58 },
          { country: 'INDIA', flag: '🇮🇳', revenue: 1850000, growthPercentage: 34 },
        ]);

      } catch (err: any) {
        setError(err.message || 'An unknown error occurred');
        console.error("Failed to fetch dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []); // Empty dependency array means this runs once on mount

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full text-xl text-gray-700">
        Loading dashboard data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full text-xl text-red-600">
        Error: {error}
      </div>
    );
  }

  // Helper to format numbers to AED M/K
  const formatCurrency = (value: number) => {
    if (value >= 0) {
      return `0`;
    } else if (value >= 0) {
      return `0`;
    }
    return `AED ${value}`;
  };

  // Helper for percentage comparison
  const calculatePercentageChange = (current: number, target: number) => {
    if (target === 0) return "N/A";
    const change = ((current - target) / target) * 100;
    return `${change.toFixed(2)}%`;
  };

  // Calculate percentage vs target for Total Sales Card
  const totalSalesPercentage = summaryData && summaryData.totalSalesTarget > 0
    ? ((summaryData.totalSales / summaryData.totalSalesTarget) * 100).toFixed(2)
    : "0.00";
  const totalSalesChangeType = summaryData && summaryData.totalSales >= summaryData.totalSalesTarget * 0.75 ? 'positive' : 'negative'; // Example logic


  return (
    <>
      {/* Top Section: Search Bar, Date, and User Info (Mimicking d.jpg top bar) */}
      <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm mb-6 border-b border-gray-100">
        <div className="relative flex-1 mr-4">
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-gray-50 text-gray-800"
          />
          <svg className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
        </div>
        <div className="text-gray-600 text-sm md:text-base hidden sm:block">
          {currentDate}
        </div>
        {/* User profile from d.jpg */}
        <div className="flex items-center ml-4">
          <img src="https://via.placeholder.com/30" alt="User Avatar" className="w-8 h-8 rounded-full mr-2 hidden md:block" />
          <div className="text-gray-800 text-sm hidden md:block">
            <p className="font-semibold">{user ? user.userName : 'Guest'}</p> {/* Dynamic User Name */}
            <p className="text-xs text-gray-500">Company Manager</p>
          </div>
        </div>
      </div>


      {/* Main Dashboard Content Area */}
      <div className="p-8 bg-white rounded-xl shadow-2xl space-y-8">

        {/* Sales Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryData && (
            <>
              <SalesSummaryCard
                title="Total Sales"
                value={formatCurrency(summaryData.totalSales)}
                percentage={`${totalSalesPercentage}%`} // Dynamic calculation
                comparisonText="vs 1.2M Target"
                changeType={totalSalesChangeType}
                icon="💵"
                progressBarValue={parseFloat(totalSalesPercentage)}
              />
              <SalesSummaryCard
                title="Total Orders"
                value={summaryData.totalOrders.toLocaleString()}
                percentage={calculatePercentageChange(summaryData.totalOrders, 17443)} // Example: vs last month's 17443 orders
                comparisonText="Vs last month"
                changeType={summaryData.totalOrders > 17443 ? 'positive' : 'negative'}
                icon="📦"
              />
              <SalesSummaryCard
                title="Order Complete"
                value={summaryData.ordersComplete.toLocaleString()}
                percentage={calculatePercentageChange(summaryData.ordersComplete, 16920)} // Example: vs last month's 16920 complete orders
                comparisonText="Vs last month"
                changeType={summaryData.ordersComplete > 16920 ? 'positive' : 'negative'}
                icon="✅"
              />
              <SalesSummaryCard
                title="Cancel Order"
                value={summaryData.cancelOrders.toLocaleString()}
                percentage={calculatePercentageChange(summaryData.cancelOrders, 227)} // Example: vs last month's 227 cancelled orders
                comparisonText="227 Design changing"
                changeType={summaryData.cancelOrders > 227 ? 'positive' : 'negative'}
                icon="❌"
              />
            </>
          )}
        </div>

        {/* Target and Statistics Chart Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-6 rounded-lg shadow-md border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Target</h2>
            {revenueTargetData && (
              <RevenueTarget
                currentPercentage={revenueTargetData.currentPercentage}
                revenueToday={revenueTargetData.revenueToday}
                currentSellsRevenue={revenueTargetData.currentSellsRevenuePercentage}
                target={formatCurrency(revenueTargetData.targetValue)}
                revenue={formatCurrency(revenueTargetData.revenueValue)}
                sells={formatCurrency(revenueTargetData.sellsValue)}
              />
            )}
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Statistic</h2>
            {salesChartData.length > 0 && <SalesStatisticsChart data={salesChartData} />}
          </div>
        </div>

        {/* Latest Order and Business Growth Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Latest Order</h2>
              <div className="flex space-x-2 text-sm">
                <button className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300">Select Date</button>
                <button className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300">Filters</button>
                <button className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300">See All</button>
              </div>
            </div>
            {latestOrders.length > 0 && <LatestOrdersTable orders={latestOrders} />}
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Business Growth</h2>
            {businessGrowth.length > 0 && <BusinessGrowthByCountry data={businessGrowth} />}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white text-center p-4 text-sm rounded-b-xl mt-8">
        &copy; {new Date().getFullYear()} Jewellery Shop Admin. All rights reserved.
      </footer>
    </>
  );
};

export default AdminDashboard;